import { z } from "zod";

import { SiteDashboardProperties } from "~/api/Api";

import { DecodedWidgetConfig } from "./DecodedWidgetConfig.type";

export type WidgetConfig = SiteDashboardProperties["widgets"][number];

/**
 * RenderableWidget type holds combination of validated widget config and data or error
 */
export type RenderableWidget = {
  id: string;
} & (
  | {
      type: "property_report";
      config: Extract<DecodedWidgetConfig, { method: "property_report" }>;
      data: GoogleAnalyticsPropertyReport;
    }
  | {
      type: "property_realtime_report";
      config: Extract<
        DecodedWidgetConfig,
        { method: "property_realtime_report" }
      >;
      data: GoogleAnalyticsPropertyRealtimeReport;
    }
  | {
      type: "search_analytics";
      config: Extract<DecodedWidgetConfig, { method: "search_analytics" }>;
      data: GoogleSearchConsoleReport;
    }
);

export type RenderableWidgetOrError =
  | RenderableWidget
  | (Pick<RenderableWidget, "id" | "config" | "type"> & {
      error: Error;
    });

export const GoogleAnalyticsPropertyReportSchema = z.object({
  dimension_headers: z
    .array(
      z.object({
        name: z.string(),
      }),
    )
    .optional(),
  kind: z.literal("analyticsData#runReport"),
  metric_headers: z
    .array(
      z.object({
        name: z.string(),
        type: z.string(),
      }),
    )
    .default([]),
  row_count: z.number().optional(),
  rows: z
    .array(
      z.object({
        dimension_values: z
          .array(
            z.object({
              value: z.string(),
            }),
          )
          .optional(),
        metric_values: z.array(
          z.object({
            value: z.string(),
          }),
        ),
      }),
    )
    .optional(),
});

export type GoogleAnalyticsPropertyReport = z.infer<
  typeof GoogleAnalyticsPropertyReportSchema
>;

export const GoogleAnalyticsPropertyRealtimeReportSchema = z.object({
  dimension_headers: z
    .array(
      z.object({
        name: z.string(),
      }),
    )
    .optional(),
  kind: z.literal("analyticsData#runRealtimeReport"),
  metric_headers: z
    .array(
      z.object({
        name: z.string(),
        type: z.string(),
      }),
    )
    .default([]),
  row_count: z.number().optional(),
  rows: z
    .array(
      z.object({
        dimension_values: z
          .array(
            z.object({
              value: z.string(),
            }),
          )
          .optional(),
        metric_values: z.array(
          z.object({
            value: z.string(),
          }),
        ),
      }),
    )
    .optional(),
});

export type GoogleAnalyticsPropertyRealtimeReport = z.infer<
  typeof GoogleAnalyticsPropertyRealtimeReportSchema
>;

export const GoogleSearchConsoleReportSchema = z.object({
  response_aggregation_type: z.enum(["byProperty", "byPage"]),
  rows: z
    .array(
      z.object({
        clicks: z.number(),
        ctr: z.number(),
        impressions: z.number(),
        position: z.number(),
        keys: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});

export type GoogleSearchConsoleReport = z.infer<
  typeof GoogleSearchConsoleReportSchema
>;
